import { createContext, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useStatisticsCapabilities } from '../../../screens/statistics/common/lib/statisticsAccess';

const ChartWorkbenchLauncherSurface = lazy(() => import('./ChartWorkbenchLauncherSurface'));

const ChartWorkbenchLauncherContext = createContext(null);

const isWorkbenchShortcut = (event) =>
	(event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && (event.key === '.' || event.code === 'Period');

/** 顶栏图表工作台：当前页弹出命令面板 / 工作台，不跳转业务统计 */
export const ChartWorkbenchLauncherProvider = ({ children }) => {
	const { canAccessDashboardSection, isScopedViewer } = useStatisticsCapabilities();
	const canOpen = canAccessDashboardSection && !isScopedViewer;
	const [paletteOpen, setPaletteOpen] = useState(false);
	const [modalAction, setModalAction] = useState(null);

	const openPalette = useCallback(() => {
		setPaletteOpen(true);
	}, []);

	const closePalette = useCallback(() => {
		setPaletteOpen(false);
	}, []);

	const openModal = useCallback((action) => {
		setPaletteOpen(false);
		setModalAction(action);
	}, []);

	const dismissModal = useCallback(() => {
		setModalAction(null);
	}, []);

	useEffect(() => {
		if (!canOpen) return undefined;

		function handler(event) {
			if (!isWorkbenchShortcut(event)) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			setPaletteOpen((open) => !open);
		}

		document.addEventListener('keydown', handler, true);
		return () => document.removeEventListener('keydown', handler);
	}, [canOpen]);

	const openEdit = useCallback((chartId) => {
		if (!chartId) return;
		setPaletteOpen(false);
		setModalAction({ type: 'edit', chartId });
	}, []);

	const value = useMemo(() => ({ canOpen, openPalette, openEdit }), [canOpen, openEdit, openPalette]);
	const surfaceOpen = canOpen && (paletteOpen || Boolean(modalAction));

	return (
		<ChartWorkbenchLauncherContext.Provider value={value}>
			{children}
			{surfaceOpen ? (
				<Suspense fallback={null}>
					<ChartWorkbenchLauncherSurface
						paletteOpen={paletteOpen}
						modalAction={modalAction}
						onClosePalette={closePalette}
						onOpenModal={openModal}
						onModalDismiss={dismissModal}
					/>
				</Suspense>
			) : null}
		</ChartWorkbenchLauncherContext.Provider>
	);
};

ChartWorkbenchLauncherProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export const useChartWorkbenchLauncher = () => useContext(ChartWorkbenchLauncherContext);
